import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import {
  Alert,
  Button,
  Descriptions,
  Input,
  Modal,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';
import {
  useGetPhdSchoolYearTransitionPreviewQuery,
  useTransitionPhdSchoolYearMutation,
} from '../../../../../../redux/services/phd';
import PhdSettingsSection from './PhdSettingsSection.jsx';
import { useToastMessages } from '../../../../../../hooks/useToastMessages.jsx';
import { ROLE_PHD_ADMIN } from '../../../../../../utils/constants/roles.js';

const BLOCKER_LABELS = {
  invalid_current_year_count: 'Systém nemá práve jeden aktuálny školský rok.',
  transition_too_early:
    'Prechod je možný až od prvého dňa nasledujúceho školského roka.',
  target_interval_conflict:
    'Cieľový interval je v konflikte s existujúcim školským rokom.',
  missing_annual_evaluation: 'Doktorand nemá kompletné ročné hodnotenie.',
  missing_annual_credits: 'Doktorand nemá kompletne odovzdané ročné známky.',
  invalid_school_year_interval:
    'Školský rok nemá povolený interval od 1. septembra do 31. augusta.',
  invalid_school_year_status: 'Stav školského roka nie je konzistentný.',
  overlapping_school_years: 'Evidované školské roky sa prekrývajú.',
  school_year_gap: 'Medzi evidovanými školskými rokmi je medzera.',
};

const BLOCKER_REPAIRS = {
  invalid_current_year_count:
    'Opravte označenie aktuálneho školského roka tak, aby bol aktuálny presne jeden rok.',
  transition_too_early:
    'Počkajte do zobrazeného najskoršieho termínu. Skoršie uzavretie školského roka nie je možné.',
  target_interval_conflict:
    'Pred opakovaním predbežnej kontroly opravte prekrývajúce sa obdobie školského roka.',
  missing_annual_evaluation:
    'Otvorte ročné hodnotenie daného doktoranda a doplňte povinné údaje; výnimočné uzavretie vyžaduje dôvod.',
  missing_annual_credits:
    'Otvorte známky daného doktoranda a dokončite odovzdanie; výnimočné uzavretie vyžaduje dôvod.',
  invalid_school_year_interval:
    'Opravte interval na 1. september až 31. august.',
  invalid_school_year_status:
    'Opravte stav školského roka a jeho označenie ako aktuálneho, potom zopakujte predbežnú kontrolu.',
  overlapping_school_years: 'Odstráňte prekrytie intervalov školských rokov.',
  school_year_gap: 'Doplňte chýbajúci nadväzujúci školský rok bez medzery.',
};

const normalizeRoleName = (name) =>
  String(name || '')
    .toLowerCase()
    .replace(/[.\s]+/g, ' ')
    .trim();

const yearLabel = (year) =>
  year
    ? `${String(year.start_year).slice(0, 4)}/${String(year.end_year).slice(0, 4)}`
    : '–';

const PhdSchoolYearTransitionSection = () => {
  const { t } = useTranslation();
  const user = useSelector((state) => state.auth.user);
  const hasAdminRole = useMemo(
    () =>
      Array.isArray(user?.roles) &&
      user.roles.some(
        (role) =>
          normalizeRoleName(typeof role === 'string' ? role : role?.name) ===
          normalizeRoleName(ROLE_PHD_ADMIN),
      ),
    [user?.roles],
  );
  const canTransition =
    hasAdminRole &&
    Object.prototype.hasOwnProperty.call(
      user?.permissions || {},
      'phd-school-years.transition',
    );
  const { data, isLoading, error } = useGetPhdSchoolYearTransitionPreviewQuery(
    undefined,
    {
      skip: !canTransition,
    },
  );
  const [transition, { isLoading: isTransitioning }] =
    useTransitionPhdSchoolYearMutation();
  const [open, setOpen] = useState(false);
  const [exceptionReason, setExceptionReason] = useState('');
  const [lastResult, setLastResult] = useState(null);
  const { successMessage, saveFailed } = useToastMessages();
  const domainBlockers = (data?.blockers || []).filter(
    (item) => !item.structural,
  );
  const structuralBlockers = (data?.blockers || []).filter(
    (item) => item.structural,
  );

  if (!canTransition) {
    return null;
  }

  const closeModal = () => {
    setOpen(false);
    setExceptionReason('');
  };

  const renderIssues = (items) => (
    <ul style={{ margin: 0, paddingInlineStart: 20 }}>
      {items.map((item, index) => (
        <li
          key={`${item.key}-${item.phd_student_id ?? 'none'}-${item.school_year_id ?? 'none'}-${index}`}
          style={{ marginBottom: 8 }}
        >
          <Space orientation="vertical" size={2}>
            <Typography.Text>
              {t(BLOCKER_LABELS[item.key] || item.key)}
            </Typography.Text>
            <Typography.Text type="secondary">
              {t(
                BLOCKER_REPAIRS[item.key] ||
                  'Skontrolujte zdrojové údaje a zopakujte predbežnú kontrolu.',
              )}
            </Typography.Text>
            <Space size={4} wrap>
              {item.structural ? (
                <Tag color="red">{t('Dátová integrita')}</Tag>
              ) : (
                <Tag color="orange">{t('Ročná povinnosť')}</Tag>
              )}
              {item.phd_student_id ? (
                <Tag>
                  {t('Doktorand ID: {{id}}', { id: item.phd_student_id })}
                </Tag>
              ) : null}
              {item.school_year_id ? (
                <Tag>
                  {t('Školský rok ID: {{id}}', { id: item.school_year_id })}
                </Tag>
              ) : null}
              {item.count !== undefined ? (
                <Tag>{t('Počet: {{count}}', { count: item.count })}</Tag>
              ) : null}
            </Space>
          </Space>
        </li>
      ))}
    </ul>
  );

  const submit = async () => {
    const isForced = domainBlockers.length > 0;

    try {
      const result = await transition({
        force: isForced,
        exception_reason: isForced ? exceptionReason.trim() : null,
      }).unwrap();
      setLastResult({ ...result, was_forced: isForced });
      successMessage({
        message: t('Školský rok bol bezpečne uzavretý'),
        description: t('Aktuálny školský rok: {{year}}', {
          year: yearLabel(result.current_year),
        }),
      });
      closeModal();
    } catch (requestError) {
      saveFailed(requestError);
    }
  };

  return (
    <PhdSettingsSection
      title={t('Príprava na nový školský rok')}
      description={t(
        'Uzavrie aktuálny rok, zmrazí ročné dáta a aktivuje presne nasledujúci rok.',
      )}
    >
      {lastResult ? (
        <Alert
          type="success"
          showIcon
          closable
          onClose={() => setLastResult(null)}
          style={{ marginBottom: 16 }}
          title={t('Posledný prechod bol dokončený')}
          description={
            <Descriptions size="small" column={{ xs: 1, md: 2 }}>
              <Descriptions.Item label={t('Číslo uzávierky')}>
                {lastResult.closure_id}
              </Descriptions.Item>
              <Descriptions.Item label={t('Spôsob uzavretia')}>
                {lastResult.was_forced
                  ? t('Výnimočné uzavretie s dôvodom')
                  : t('Riadne uzavretie')}
              </Descriptions.Item>
              <Descriptions.Item label={t('Uzavretý školský rok')}>
                {yearLabel(lastResult.closed_year)}
              </Descriptions.Item>
              <Descriptions.Item label={t('Aktivovaný školský rok')}>
                {yearLabel(lastResult.current_year)}
              </Descriptions.Item>
              <Descriptions.Item label={t('Zmrazené ročné záznamy')}>
                {lastResult.summary?.archive_counts?.total ?? 0}
              </Descriptions.Item>
              <Descriptions.Item label={t('Dotknutí doktorandi')}>
                {lastResult.summary?.student_counts?.affected ?? 0}
              </Descriptions.Item>
            </Descriptions>
          }
        />
      ) : null}

      {isLoading ? <Spin /> : null}
      {error ? (
        <Alert
          type="error"
          showIcon
          title={t('Predbežnú kontrolu sa nepodarilo načítať')}
          description={t(
            'Skontrolujte pripojenie a oprávnenie, potom obnovte túto časť stránky. Školský rok uzavrite až po úspešnej predbežnej kontrole.',
          )}
        />
      ) : null}
      {data ? (
        <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
          <Descriptions bordered size="small" column={{ xs: 1, md: 2 }}>
            <Descriptions.Item label={t('Aktuálny školský rok')}>
              {yearLabel(data.current_year)}
            </Descriptions.Item>
            <Descriptions.Item label={t('Nasledujúci školský rok')}>
              {yearLabel(data.target_year)}
            </Descriptions.Item>
            <Descriptions.Item label={t('Najskorší termín prechodu')}>
              {data.earliest_transition_date
                ? dayjs(data.earliest_transition_date).format('DD.MM.YYYY')
                : '–'}
            </Descriptions.Item>
            <Descriptions.Item label={t('Predbežná kontrola vykonaná')}>
              {data.previewed_at
                ? dayjs(data.previewed_at).format('DD.MM.YYYY HH:mm')
                : '–'}
            </Descriptions.Item>
            <Descriptions.Item label={t('Aktívni doktorandi')}>
              {data.student_counts?.active ?? 0}
            </Descriptions.Item>
            <Descriptions.Item label={t('Prerušení doktorandi')}>
              {data.student_counts?.interrupted ?? 0}
            </Descriptions.Item>
            <Descriptions.Item label={t('Ročné hodnotenia na zmrazenie')}>
              {data.archive_counts?.evaluations ?? 0}
            </Descriptions.Item>
            <Descriptions.Item label={t('Záznamy známok na zmrazenie')}>
              {data.archive_counts?.grades ?? 0}
            </Descriptions.Item>
            <Descriptions.Item label={t('Chýbajúce ročné záznamy')}>
              {data.missing_annual_records?.length ?? 0}
            </Descriptions.Item>
            <Descriptions.Item label={t('Spolu záznamov na zmrazenie')}>
              {data.archive_counts?.total ?? 0}
            </Descriptions.Item>
          </Descriptions>

          {data.warnings?.length ? (
            <Alert
              type="warning"
              showIcon
              title={t('Upozornenia predbežnej kontroly')}
              description={renderIssues(data.warnings)}
            />
          ) : null}

          {data.blockers?.length ? (
            <Alert
              type={structuralBlockers.length ? 'error' : 'warning'}
              showIcon
              title={
                structuralBlockers.length
                  ? t(
                      'Predbežná kontrola zistila chyby v údajoch, ktoré bránia uzavretiu',
                    )
                  : t('Predbežná kontrola zistila nesplnené ročné povinnosti')
              }
              description={renderIssues(data.blockers)}
            />
          ) : (
            <Alert
              type="success"
              showIcon
              title={t('Predbežná kontrola nezistila prekážky uzavretia')}
            />
          )}

          <Button
            danger
            type="primary"
            disabled={structuralBlockers.length > 0 || isTransitioning}
            onClick={() => setOpen(true)}
          >
            {t('Uzavrieť rok a aktivovať nasledujúci')}
          </Button>
        </Space>
      ) : null}

      <Modal
        title={t('Potvrdiť nezvratný prechod školského roku')}
        open={open}
        okText={t('Uzavrieť školský rok')}
        okButtonProps={{
          danger: true,
          loading: isTransitioning,
          disabled: domainBlockers.length > 0 && !exceptionReason.trim().length,
        }}
        onCancel={closeModal}
        onOk={submit}
      >
        <Alert
          type="error"
          showIcon
          title={t(
            'Uzavreté ročné dáta už nebude možné priamo meniť ani mazať.',
          )}
        />
        {domainBlockers.length > 0 ? (
          <>
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 16 }}
              title={t(
                'Výnimočné uzavretie preskočí iba chýbajúce ročné povinnosti. Dátovú integritu nikdy neobíde.',
              )}
            />
            <Input.TextArea
              style={{ marginTop: 16 }}
              rows={4}
              value={exceptionReason}
              placeholder={t('Povinný dôvod výnimočného uzavretia')}
              onChange={(event) => setExceptionReason(event.target.value)}
              maxLength={4000}
              showCount
            />
          </>
        ) : null}
      </Modal>
    </PhdSettingsSection>
  );
};

export default PhdSchoolYearTransitionSection;
